import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Search, X, Navigation } from 'lucide-react';

// Ubicaciones predefinidas de Costa Rica para el demo
const COSTA_RICA_LOCATIONS = [
  { name: "San José Centro", city: "San José", province: "San José", lat: 9.9281, lng: -84.0907 },
  { name: "Escazú", city: "Escazú", province: "San José", lat: 9.9274, lng: -84.1395 },
  { name: "Santa Ana", city: "Santa Ana", province: "San José", lat: 9.9326, lng: -84.1826 },
  { name: "Heredia Centro", city: "Heredia", province: "Heredia", lat: 10.0024, lng: -84.1170 },
  { name: "Alajuela Centro", city: "Alajuela", province: "Alajuela", lat: 10.0162, lng: -84.2117 },
  { name: "Cartago Centro", city: "Cartago", province: "Cartago", lat: 9.8644, lng: -83.9194 },
  { name: "Curridabat", city: "Curridabat", province: "San José", lat: 9.9167, lng: -84.0333 },
  { name: "Moravia", city: "Moravia", province: "San José", lat: 9.9594, lng: -84.0517 },
  { name: "Tibás", city: "Tibás", province: "San José", lat: 9.9533, lng: -84.0833 },
  { name: "Desamparados", city: "Desamparados", province: "San José", lat: 9.8989, lng: -84.0628 },
  { name: "Guadalupe", city: "Goicoechea", province: "San José", lat: 9.9472, lng: -84.0597 },
  { name: "San Pedro", city: "Montes de Oca", province: "San José", lat: 9.9333, lng: -84.05 },
  { name: "Sabana", city: "San José", province: "San José", lat: 9.9356, lng: -84.1039 },
  { name: "Rohrmoser", city: "San José", province: "San José", lat: 9.9406, lng: -84.1167 },
  { name: "Zapote", city: "San José", province: "San José", lat: 9.9167, lng: -84.0667 },
  { name: "Tres Ríos", city: "La Unión", province: "Cartago", lat: 9.9058, lng: -83.9867 },
  { name: "San Francisco de Dos Ríos", city: "San José", province: "San José", lat: 9.9083, lng: -84.0583 },
  { name: "Pavas", city: "San José", province: "San José", lat: 9.9472, lng: -84.1278 },
  { name: "Hatillo", city: "San José", province: "San José", lat: 9.9194, lng: -84.1028 },
  { name: "Barrio Escalante", city: "San José", province: "San José", lat: 9.9367, lng: -84.0678 },
  { name: "La Sabana", city: "San José", province: "San José", lat: 9.9361, lng: -84.1056 },
  { name: "Coronado", city: "Vázquez de Coronado", province: "San José", lat: 9.9847, lng: -84.0028 },
  { name: "Santo Domingo", city: "Santo Domingo", province: "Heredia", lat: 9.9833, lng: -84.0833 },
  { name: "Belén", city: "Belén", province: "Heredia", lat: 9.9761, lng: -84.1856 },
  { name: "San Joaquín de Flores", city: "Flores", province: "Heredia", lat: 10.0, lng: -84.15 },
  { name: "Barva", city: "Barva", province: "Heredia", lat: 10.0167, lng: -84.1167 },
  { name: "San Isidro de Heredia", city: "San Isidro", province: "Heredia", lat: 10.0167, lng: -84.05 },
  { name: "Grecia", city: "Grecia", province: "Alajuela", lat: 10.0728, lng: -84.3133 },
  { name: "San Ramón", city: "San Ramón", province: "Alajuela", lat: 10.0872, lng: -84.4706 },
  { name: "Paraíso", city: "Paraíso", province: "Cartago", lat: 9.8333, lng: -83.8667 },
];

const LocationAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Buscar ubicación...",
  label,
  required = false,
  className = ""
}) => {
  const [inputValue, setInputValue] = useState(value?.address || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update input when value prop changes
  useEffect(() => {
    if (value?.address) {
      setInputValue(value.address);
    }
  }, [value]);

  const handleInputChange = (e) => {
    const query = e.target.value;
    setInputValue(query);
    
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    
    // Normalize function to remove accents for matching
    const normalizeText = (text) => {
      return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    };
    
    const normalizedQuery = normalizeText(query);
    
    // Filter locations based on query (accent-insensitive)
    const filtered = COSTA_RICA_LOCATIONS.filter(loc => 
      normalizeText(loc.name).includes(normalizedQuery) ||
      normalizeText(loc.city).includes(normalizedQuery) ||
      normalizeText(loc.province).includes(normalizedQuery)
    ).slice(0, 6);
    
    setSuggestions(filtered);
    setShowSuggestions(true);
    setIsLoading(false);
  };

  const handleSelectLocation = (location) => {
    const selectedLocation = {
      address: `${location.name}, ${location.city}`,
      city: location.city,
      province: location.province,
      lat: location.lat,
      lng: location.lng
    };
    
    setInputValue(selectedLocation.address);
    setShowSuggestions(false);
    onChange(selectedLocation);
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    onChange(null);
    inputRef.current?.focus();
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          
          // Find closest predefined location
          let closest = COSTA_RICA_LOCATIONS[0];
          let minDist = Infinity;
          
          COSTA_RICA_LOCATIONS.forEach(loc => {
            const dist = Math.sqrt(
              Math.pow(loc.lat - latitude, 2) + 
              Math.pow(loc.lng - longitude, 2)
            );
            if (dist < minDist) {
              minDist = dist;
              closest = loc;
            }
          });
          
          const location = {
            address: `Cerca de ${closest.name}, ${closest.city}`,
            city: closest.city,
            province: closest.province,
            lat: latitude,
            lng: longitude
          };
          
          setInputValue(location.address);
          onChange(location);
          setIsLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setIsLoading(false);
        },
        { enableHighAccuracy: true }
      );
    }
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {label && (
        <label className="text-sm font-medium text-slate-700 mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
          <MapPin className="w-4 h-4" />
        </div>
        
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 2 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="pl-10 pr-20"
          data-testid="location-autocomplete-input"
        />
        
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            className="p-1 hover:bg-purple-100 rounded text-purple-500 hover:text-purple-700"
            title="Usar mi ubicación actual"
          >
            <Navigation className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((location) => (
            <button
              key={`loc-${location.lat}-${location.lng}-${location.name}`}
              type="button"
              onClick={() => handleSelectLocation(location)}
              className="w-full px-4 py-3 text-left hover:bg-purple-50 flex items-start gap-3 border-b border-slate-100 last:border-0"
            >
              <MapPin className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-slate-900">{location.name}</p>
                <p className="text-sm text-slate-500">{location.city}, {location.province}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* Loading state */}
      {isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-4 text-center text-slate-500">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600 mx-auto"></div>
        </div>
      )}
      
      {/* No results */}
      {showSuggestions && inputValue.length >= 2 && suggestions.length === 0 && !isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-4 text-center text-slate-500">
          <Search className="w-5 h-5 mx-auto mb-2 text-slate-400" />
          <p className="text-sm">No se encontraron ubicaciones</p>
          <p className="text-xs text-slate-400 mt-1">Intenta con otro término</p>
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
